import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

/**
 * NotFoundPage Component (Fixed Version)
 *
 * This version of the NotFoundPage component uses CSS variables instead of
 * styled-components' theme prop to avoid the "Cannot read properties of undefined" error.
 *
 * It follows Module M7 (Integration Points) principles by providing a clear
 * integration point between the component and the theme system.
 */

// Use CSS variables instead of theme props
const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-3xl, 48px) var(--spacing-md, 16px);
  max-width: 768px;
  margin: 0 auto;
  min-height: 60vh;
`;

const ErrorCode = styled.h1`
  font-size: var(--font-size-6xl, 48px);
  font-weight: var(--font-weight-bold, 700);
  color: var(--primary-500);
  margin-bottom: var(--spacing-md, 16px);
`;

const Title = styled.h2`
  font-size: var(--font-size-3xl, 30px);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--text-primary);
  margin-bottom: var(--spacing-lg, 24px);
`;

const Description = styled.p`
  font-size: var(--font-size-lg, 18px);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl, 32px);
  max-width: 500px;
`;

const ButtonContainer = styled.div`
  margin-top: var(--spacing-lg, 24px);
`;

// Styled Link that looks like a button
const ButtonLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background-color: var(--primary-500);
  color: white;
  border: 1px solid var(--primary-500);
  border-radius: 4px;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: var(--font-size-lg, 18px);
  font-weight: var(--font-weight-medium, 500);
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: var(--primary-600);
    border-color: var(--primary-600);
  }
  
  &:active {
    background-color: var(--primary-700);
    border-color: var(--primary-700);
  }
  
  &:focus {
    box-shadow: 0 0 0 3px var(--primary-200);
    outline: 2px solid var(--primary-500);
    outline-offset: 1px;
  }
`;

/**
 * NotFoundPage component for, 404, errors
 */
const NotFoundPage: React.FC = () => {
  return (
    <NotFoundContainer role="main" aria-labelledby="error-title">
      <ErrorCode aria-hidden="true">404</ErrorCode>
      <div id="error-title"></div>
      <Description>
        The page you are looking for might have been removed had its, name changed,
        or is temporarily unavailable.
      </Description>
      <ButtonContainer>
        <ButtonLink
          to="/"
          aria-label="Return to Home Page"
        >
          Return to Home
        </ButtonLink>
      </ButtonContainer>
    </NotFoundContainer>
  );
};

export default NotFoundPage;
